import "./DeveloperProjectCardWithTabsTemplate.css";

import {
  InteractionObjectType,
  OrganisationTypeConstants,
  ProjectPermissionConstants,
  StandardConstants,
} from "../../constants";
import { NavigationActiveElement, TabsList } from "../../models";
import {
  Carousel,
  Column,
  ExpandableCard,
  InformationBox,
  KanaTooltip,
  LeftNavigation,
  OrganisationIcon,
  PieChart,
  Pill,
  ProfileMenu,
  ProfileMenuItem,
  QuickActionsMenu,
  Row,
  ScrollablePills,
  Tabs,
  UserHeader,
} from "../../widget";
import { NotesPanel } from "../NotesPanel";
import { TagsPanel } from "../TagsPanel";
import {
  DeleteProjectModal,
  MergeProjectsModal,
  MultiDraftModal,
  PublishProjectModal,
  VersionConflictModal,
} from "./components";
import { CancelProjectModal } from "./components/CancelProjectModal";
import { UpdateStatusModal } from "./components/UpdateStatusModal";
import { useDeveloperProjectCardWithTabsTemplate } from "./useDeveloperProjectCardWithTabsTemplate";

interface DeveloperProjectCardWithTabsTemplateProps {
  activeElement: NavigationActiveElement;
  pageTitle: string | undefined;
  projectCode: string | undefined;
  projectVerifier: string | undefined;
  projectDeveloper?: string | undefined;
  projectDeveloperIcon?: string | undefined;
  projectStatus: string | undefined;
  projectUuid: string | undefined;
  projectUnitMeasurement?: string | undefined;
  chartData: { [key: string]: number };
  tabsList: TabsList;
  backButtonText?: string;
  handleBackClick?: () => void;
}

export const DeveloperProjectCardWithTabsTemplate = ({
  activeElement,
  pageTitle,
  projectCode,
  projectVerifier,
  projectDeveloper,
  projectDeveloperIcon,
  projectStatus,
  projectUuid,
  projectUnitMeasurement,
  chartData,
  tabsList,
  backButtonText,
  handleBackClick,
}: DeveloperProjectCardWithTabsTemplateProps): JSX.Element => {
  const {
    setShowQuickActionMenu,
    showQuickActionMenu,
    setShowMergeProjectsModal,
    showMergeProjectsModal,
    setShowPublishProjectModal,
    showPublishProjectModal,
    setShowDeleteProjectModal,
    showDeleteProjectModal,
    setShowMultiDraftModal,
    showMultiDraftModal,
    setShowVersionConflictModal,
    showVersionConflictModal,
    setShowNotesPanel,
    showNotesPanel,
    showTagsPanel,
    setShowTagsPanel,
    showUpdateStatusModal,
    setShowUpdateStatusModal,
    showCancelProjectModal,
    setShowCancelProjectModal,
    draftActivityHistory,
    newVersionActivityHistory,
    draftActivityHistoryUuid,
    quickActionsMenuOptionsList,
    carouselActivities,
    tags,
    navigateToDraftActivity,
    resumeDraft,
    isExternalProject,
    hasProjectPermission,
    shouldRefreshActivities,
    shouldRefreshProjectDetails,
    shouldRefreshTags,
    currentUserType,
    isGroupProject,
  } = useDeveloperProjectCardWithTabsTemplate(projectStatus, projectUuid);

  return (
    <div className="DeveloperProjectCardWithTabsTemplate">
      <div className="DeveloperProjectCardWithTabsTemplateNavigation">
        <LeftNavigation activeElement={activeElement} />
      </div>
      <div className="DeveloperProjectCardWithTabsTemplateContainer">
        <Row spacingV="ll">
          <Column span={12}>
            <UserHeader backButtonText={backButtonText} handleBackClick={handleBackClick} />
          </Column>
        </Row>
        {draftActivityHistory && (
          <Row spacingV="l">
            <Column span={12}>
              <InformationBox variant="info">
                <div className="ProjectActivityNotificationContent">
                  <p className="body2">You have a draft activity that has not been saved. You can resume the draft </p>
                  <button className="BtnLink body2" onClick={navigateToDraftActivity}>
                    here
                  </button>
                </div>
              </InformationBox>
            </Column>
          </Row>
        )}
        <Row spacingV={(projectDeveloper && projectDeveloperIcon) || projectCode || projectVerifier ? "m" : "ll"}>
          <Column span={12}>
            <div className="HeaderContent">
              <h1>{pageTitle}</h1>
              {quickActionsMenuOptionsList.length > 0 && (
                <div>
                  <QuickActionsMenu onClick={() => setShowQuickActionMenu(true)} />
                  <ProfileMenu
                    show={showQuickActionMenu}
                    onClose={() => setShowQuickActionMenu(false)}
                    position={{ top: "0" }}
                  >
                    {quickActionsMenuOptionsList.map((el) => (
                      <ProfileMenuItem
                        key={el.id}
                        profileMenuItem={el}
                        onClick={() => {
                          if (el.action) {
                            el.action();
                          }
                          setShowQuickActionMenu(false);
                        }}
                      />
                    ))}
                  </ProfileMenu>
                </div>
              )}
            </div>
          </Column>
        </Row>
        {(projectCode || projectVerifier || projectDeveloper || projectDeveloperIcon) && (
          <Row spacingV="ll">
            <Column
              span={
                currentUserType === OrganisationTypeConstants.DEVELOPMENT_MANAGER ||
                currentUserType === OrganisationTypeConstants.DEVELOPER
                  ? 8
                  : 12
              }
            >
              <div className="ProjectVerifierAndCode">
                {(projectDeveloper || projectDeveloperIcon) && (
                  <div className="DeveloperInfo">
                    {projectDeveloperIcon && <OrganisationIcon organisationIcon={projectDeveloperIcon} />}
                    <p className="body1">{projectDeveloper}</p>
                  </div>
                )}
                {projectCode && <Pill label={projectCode} variantMap={StandardConstants.CODE_PILL_VARIANT_MAP} />}
                {projectVerifier && <Pill label={projectVerifier} variant="grey-clear" />}
              </div>
            </Column>
            {(currentUserType === OrganisationTypeConstants.DEVELOPMENT_MANAGER ||
              currentUserType === OrganisationTypeConstants.DEVELOPER) && (
              <Column span={4} justify="end">
                <ScrollablePills pills={tags} linkText="Tags" linkOnClick={() => setShowTagsPanel(true)} />
              </Column>
            )}
          </Row>
        )}
        <Row spacingV="ll">
          <Column span={7}>
            <Carousel
              status={projectStatus}
              activities={carouselActivities || []}
              canManageActivity={hasProjectPermission(ProjectPermissionConstants.MANAGE_PROJECT_ACTIVITY)}
              isExternal={isExternalProject()}
              shouldRefreshActivities={shouldRefreshActivities}
              shouldRefreshProjectDetails={shouldRefreshProjectDetails}
            />
          </Column>
          <Column span={5}>
            <ExpandableCard
              title={projectUnitMeasurement === "Biodiversity" ? "BNG units" : "Units"}
              contentAlignment="center"
              tooltip={
                <KanaTooltip
                  tooltipHeader="Units"
                  tooltipText="Please note that only 'issued' units show here, not units listed as 'draft' or 'inactive'"
                />
              }
              height="100%"
            >
              <PieChart
                size={120}
                data={chartData}
                noDataMessage="No units issued"
                isGradient={[
                  OrganisationTypeConstants.DEVELOPMENT_MANAGER,
                  OrganisationTypeConstants.ASSET_MANAGER,
                ].includes(currentUserType)}
                centerTextFont={
                  currentUserType === OrganisationTypeConstants.DEVELOPMENT_MANAGER ? "Fraunces" : "Space Grotesk"
                }
              />
            </ExpandableCard>
          </Column>
        </Row>
        <Tabs className="TemplateTabs" tabsList={tabsList} currentUserType={currentUserType} />
      </div>
      <MergeProjectsModal
        showMergeProjectsModal={showMergeProjectsModal}
        closeModal={() => setShowMergeProjectsModal(false)}
      />
      <PublishProjectModal
        showPublishProjectModal={showPublishProjectModal}
        closeModal={() => setShowPublishProjectModal(false)}
      />
      <UpdateStatusModal
        existingStatus={projectStatus ?? ""}
        isGroupProject={isGroupProject}
        showUpdateStatusModal={showUpdateStatusModal}
        closeModal={() => setShowUpdateStatusModal(false)}
      />
      <CancelProjectModal
        isGroupProject={isGroupProject}
        showCancelProjectModal={showCancelProjectModal}
        closeModal={() => setShowCancelProjectModal(false)}
        refreshProject={shouldRefreshProjectDetails}
      />
      <DeleteProjectModal
        showDeleteProjectModal={showDeleteProjectModal}
        closeModal={() => setShowDeleteProjectModal(false)}
      />
      <MultiDraftModal
        showMultiDraftModal={showMultiDraftModal}
        resume={resumeDraft}
        closeModal={() => setShowMultiDraftModal(false)}
        draftActivityHistory={draftActivityHistory}
      />
      <VersionConflictModal
        showVersionConflictModal={showVersionConflictModal}
        draftActivityHistoryUuid={draftActivityHistoryUuid}
        closeModal={() => setShowVersionConflictModal(false)}
        newVersionActivityHistory={newVersionActivityHistory}
      />
      {projectUuid &&
        (currentUserType === OrganisationTypeConstants.DEVELOPMENT_MANAGER ||
          currentUserType === OrganisationTypeConstants.DEVELOPER) && (
          <NotesPanel
            objectUuid={projectUuid}
            objectType={InteractionObjectType.PROJECT}
            show={showNotesPanel}
            onClose={() => setShowNotesPanel(false)}
          />
        )}
      {projectUuid &&
        (currentUserType === OrganisationTypeConstants.DEVELOPMENT_MANAGER ||
          currentUserType === OrganisationTypeConstants.DEVELOPER) && (
          <TagsPanel
            objectUuid={projectUuid}
            objectType={InteractionObjectType.PROJECT}
            show={showTagsPanel}
            onClose={() => setShowTagsPanel(false)}
            shouldRefreshTags={shouldRefreshTags}
          />
        )}
    </div>
  );
};
